import { useCallback, useEffect, useRef, useState } from 'react';

import { LinearScriptPlayer } from '@core/assistant/providers/scriptPlayer/linearScriptPlayer';

import { TutorialContext } from './context';
import { ITutorialProviderProps } from './interfaces';
import { TutorialHighlightKey, TutorialId } from '../interfaces';
import { TUTORIAL_REGISTRY } from '../registry';
import { buildScriptNodes } from '../utils/buildScriptNodes';
import {
  getCompletedTutorials,
  markTutorialCompleted,
} from '../utils/completedTutorials';

/**
 * Owns guided-tutorial playback: the active `LinearScriptPlayer` instance,
 * the currently highlighted `PulsingBorder` target, the currently shown
 * `KeyCap` key hint, the current step's narration text, whether the active
 * tutorial's first step has begun narrating, the set of tutorials
 * completed so far (persisted in localStorage), the tutorial (if any) whose
 * completion notice should currently show, and the in-flight narration audio
 * element. Exposes start/stop controls and that state via `TutorialContext`.
 *
 * @param props.children - Subtree that can call `useTutorial()`/`useTutorialHighlight()`/`useTutorialKeyHint()`/`useTutorialStepText()`/`useCompletedTutorials()`.
 */
const TutorialProvider = ({ children }: ITutorialProviderProps) => {
  const [activeTutorialId, setActiveTutorialId] = useState<TutorialId | null>(
    null,
  );
  const [isTutorialRunning, setIsTutorialRunning] = useState(false);
  const [activeHighlightTarget, setActiveHighlightTarget] =
    useState<TutorialHighlightKey | null>(null);
  const [activeKeyHint, setActiveKeyHint] = useState<string | null>(null);
  const [activeStepText, setActiveStepText] = useState<string | null>(null);
  const [hasNarrationStarted, setHasNarrationStarted] = useState(false);
  const [completedTutorialIds, setCompletedTutorialIds] = useState<
    TutorialId[]
  >(() => getCompletedTutorials());
  const [completedNoticeTutorialId, setCompletedNoticeTutorialId] =
    useState<TutorialId | null>(null);
  const [isPromptForced, setIsPromptForced] = useState(false);

  const playerRef = useRef<LinearScriptPlayer | undefined>(undefined);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  const releaseAudio = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
  }, []);

  const stopTutorial = useCallback(() => {
    const state = playerRef.current?.getCurrentState();

    if (state) {
      console.info('Guided tutorial: stopping', {
        tutorialId: state.name,
        currentStepId: state.currentNode?.id,
        isCompleted: state.isCompleted,
      });
    }

    releaseAudio();

    if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = undefined;
    }

    setActiveHighlightTarget(null);
    setActiveKeyHint(null);
    setActiveStepText(null);
    setHasNarrationStarted(false);
    setIsTutorialRunning(false);
    setActiveTutorialId(null);
  }, [releaseAudio]);

  const startTutorial = useCallback(
    (id: TutorialId) => {
      console.info('Guided tutorial: start requested', { tutorialId: id });

      stopTutorial();

      const definition = TUTORIAL_REGISTRY[id];

      if (!definition.steps.length) {
        console.warn('Tutorial has no steps defined, skipping playback', {
          tutorialId: id,
        });
        return;
      }

      const nodes = buildScriptNodes(definition.steps, {
        tutorialId: id,
        setHighlight: setActiveHighlightTarget,
        setKeyHint: setActiveKeyHint,
        setStepText: (text) => {
          setActiveStepText(text);
          // First step-text write per tutorial run means the first step's
          // narration attempt just finished (played or failed) — that's the
          // "skip tutorial" button's cue to appear, per the request that it
          // not show before narration has actually started.
          setHasNarrationStarted(true);
        },
        currentAudioRef,
      });

      const logStepState = (
        label: string,
        stepId: string,
        stepIndex: number,
      ) => {
        const state = playerRef.current?.getCurrentState();
        console.info(`Guided tutorial: ${label}`, {
          tutorialId: id,
          stepId,
          stepIndex,
          isRunning: state?.isRunning,
          isWaiting: state?.isWaiting,
        });
      };

      playerRef.current = new LinearScriptPlayer(nodes, {
        name: id,
        onNodeStart: (nodeId, index) => {
          logStepState('step starting', nodeId, index);
        },
        onWaitingForInput: (nodeId, index) => {
          logStepState('step ready, awaiting user input', nodeId, index);
        },
        onKeyCaptured: (_event, nodeId, index) => {
          logStepState(
            'step input captured, clearing highlight/key hint/step text',
            nodeId,
            index,
          );
          setActiveHighlightTarget(null);
          setActiveKeyHint(null);
          setActiveStepText(null);
        },
        onComplete: (lastNodeId) => {
          console.info('Guided tutorial: completed', {
            tutorialId: id,
            lastStepId: lastNodeId,
          });
          setCompletedTutorialIds(markTutorialCompleted(id));
          setCompletedNoticeTutorialId(id);
          stopTutorial();
        },
        onError: (error, nodeId) => {
          console.warn(
            `Tutorial playback failed: ${(error as Error).message}`,
            { tutorialId: id, stepId: nodeId },
          );
          stopTutorial();
        },
      });

      setActiveTutorialId(id);
      setIsTutorialRunning(true);
      console.info('Guided tutorial: started', { tutorialId: id });
      playerRef.current.start();
    },
    [stopTutorial],
  );

  const openTutorialPrompt = useCallback(() => {
    setIsPromptForced(true);
  }, []);

  const closeTutorialPrompt = useCallback(() => {
    setIsPromptForced(false);
  }, []);

  const dismissCompletionNotice = useCallback(() => {
    setCompletedNoticeTutorialId(null);
  }, []);

  useEffect(() => {
    return () => {
      stopTutorial();
    };
  }, [stopTutorial]);

  return (
    <TutorialContext.Provider
      value={{
        activeTutorialId,
        isTutorialRunning,
        activeHighlightTarget,
        activeKeyHint,
        activeStepText,
        hasNarrationStarted,
        completedTutorialIds,
        completedNoticeTutorialId,
        isPromptForced,
        startTutorial,
        stopTutorial,
        openTutorialPrompt,
        closeTutorialPrompt,
        dismissCompletionNotice,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

export default TutorialProvider;
